import { Page } from "../../components/layout/Page";
import {
  Box,
  Button,
  Checkbox,
  Input,
  Loader,
  Modal,
  Popover,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import listClasses from "./ClosedInvoice.module.css";
import listSticky from "../../components/list/list.module.css";
import {
  ArrowClockwiseIcon,
  CoinsIcon,
  DownloadSimpleIcon,
  FileTextIcon,
  ListMagnifyingGlassIcon,
  PlusIcon,
  SealCheckIcon,
  TrayIcon,
  WarningCircleIcon,
  XIcon,
} from "@phosphor-icons/react";
import qs from "qs";
import { modals } from "@mantine/modals";
import { useDebouncedValue } from "@mantine/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { EbarimtApi, InvoiceApi, NetworkApi } from "../../apis";
import {
  BulkActionBar,
  DateCell,
  DateRangeChip,
  DocNoCell,
  ListFooter,
  ListStates, resolveListStates,
  MoneyCell,
  PartyCell,
  RowActions,
} from "../../components/list";
import { usePermission } from "../../components/permission";
import { FeePayModal } from "../../components/fee-pay/fee-pay-modal";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { usePdfDownload } from "../../hooks/usePdfDownload";
import { useRowLoading } from "../../hooks/useRowLoading";
import { useRowSelection } from "../../hooks/useRowSelection";
import { SELECT_ALL_CAP, saveInvoicePdfs } from "../../utils/bulk-invoice";
import { errorReason } from "../../utils/error-handler";
import { IAuth } from "../../interfaces/IAuth";
import { currencyFormat } from "../../utils";
import { renderBusinessName } from "../../utils/render-business-name";
import { message } from "../../utils/Message";
import modalChrome from "../../components/ui/app-modal.module.css";
import { handleError } from "../../utils/error-handler";
import { dateFormat } from "../../utils/date";
import { downloadCsv, printReport } from "../../utils/table-export";

/** CSV and the printed report cover every closed invoice matching the filters, not one page. */
const EXPORT_LIMIT = 5000;

/** Checkbox + actions + 6 data columns (+ fee for a supplier). */
const COL_COUNT = 9;

type ClosedQuery = {
  query: string;
  startDate: string | null;
  endDate: string | null;
  counterpartyBusinessId: string | null;
  page: number;
  limit: number;
};

const daysBetween = (a?: string | null, b?: string | null) => {
  if (!a || !b) return null;
  const d1 = new Date(a).getTime();
  const d2 = new Date(b).getTime();
  if (!isFinite(d1) || !isFinite(d2)) return null;
  return Math.round((d2 - d1) / 86400000);
};

function readQuery(params: URLSearchParams): ClosedQuery {
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const limit = Math.max(1, parseInt(params.get("limit") || "40", 10) || 40);
  return {
    query: params.get("query") || "",
    startDate: params.get("from") || null,
    endDate: params.get("to") || null,
    counterpartyBusinessId: params.get("partner") || null,
    page,
    limit,
  };
}

function writeQuery(q: ClosedQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.query) p.set("query", q.query);
  if (q.startDate) p.set("from", q.startDate);
  if (q.endDate) p.set("to", q.endDate);
  if (q.counterpartyBusinessId) p.set("partner", q.counterpartyBusinessId);
  if (q.page !== 1) p.set("page", String(q.page));
  if (q.limit !== 40) p.set("limit", String(q.limit));
  return p;
}

export const ClosedInvoiceList = () => {
  const { t } = useTranslation(["invoice", "common"]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const INV_LIST_CLOSED = usePermission("INV_LIST_CLOSED");
  const INV_LIST_CLOSED_GET = usePermission("INV_LIST_CLOSED_GET");
  const INV_LIST_CLOSED_PDF = usePermission("INV_LIST_CLOSED_PDF");
  const INV_LIST_CLOSED_EBARIMT = usePermission("INV_LIST_CLOSED_EBARIMT");

  const myBusinessType = useSelector((state: { auth: IAuth }) => state.auth.myBusinessType);
  // A buyer pays no DeHUB fee on its closed invoices and its counterparty is the supplier.
  const isBuyer = myBusinessType === "BUYER";

  const { addLoad: addEload, removeLoad: removeEload, isLoading: isEloading } = useRowLoading();
  const { addLoad: addEReturn, removeLoad: removeEReturn, isLoading: isEReturning } = useRowLoading();
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [dataChanged, setDataChanged] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [partnerLabel, setPartnerLabel] = useState<string | null>(null);

  const q = useMemo(() => readQuery(searchParams), [searchParams]);

  const patchQuery = useCallback(
    (patch: Partial<ClosedQuery>, resetPage = true) => {
      const next: ClosedQuery = {
        ...q,
        ...patch,
        page: resetPage ? 1 : (patch.page ?? q.page),
      };
      setSearchParams(writeQuery(next), { replace: true });
    },
    [q, setSearchParams],
  );

  const [searchInput, setSearchInput] = useState(q.query);
  useEffect(() => {
    setSearchInput(q.query);
  }, [q.query]);
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);
  useEffect(() => {
    if (debouncedSearch === q.query) return;
    patchQuery({ query: debouncedSearch });
  }, [debouncedSearch, q.query, patchQuery]);

  const [feeModal, setFeeModal] = useState<{ open: boolean; scopeFeeId: string | null }>({
    open: false,
    scopeFeeId: null,
  });

  const { download: downloadPdf, isLoading: isPdfLoading } = usePdfDownload({ fetcher: InvoiceApi.pdf });

  const offset = useMemo(() => ({ page: q.page, limit: q.limit }), [q.page, q.limit]);
  const apiFilter = useMemo(
    () => ({
      query: q.query || null,
      startDate: q.startDate,
      endDate: q.endDate,
      counterpartyBusinessId: q.counterpartyBusinessId,
    }),
    [q.query, q.startDate, q.endDate, q.counterpartyBusinessId],
  );

  const swrKey = INV_LIST_CLOSED.hasAccess
    ? `inv.invoice.closed?${qs.stringify({ offset, filter: apiFilter })}${dataChanged ? ".changed" : ""}`
    : null;

  const { data, isLoading, error, mutate } = useSwr<any>(
    swrKey,
    async () =>
      await InvoiceApi.closeWithFee({
        offset,
        filter: apiFilter,
      } as any),
  );

  const { data: unpaidFees } = useSwr(
    INV_LIST_CLOSED.hasAccess && !isBuyer ? "inv.fees.unpaid" : null,
    async () => InvoiceApi.feesUnpaid() as Promise<{ count?: number }>,
    { revalidateOnFocus: false },
  );
  const unpaidFeeCount = Number(unpaidFees?.count ?? 0);
  const feeBtnDisabled = unpaidFees != null && unpaidFeeCount === 0;

  const {
    query: partnerSearch,
    setQuery: setPartnerSearch,
    data: partnerSelects,
    isLoading: partnerLoading,
  } = useDebouncedSearch<any>({
    key: "inv.closed.partner.select",
    fetcher: (query) => NetworkApi.select({ query }),
    delay: 300,
    enabled: partnerOpen || !!q.counterpartyBusinessId,
  });

  useEffect(() => {
    if (!q.counterpartyBusinessId) {
      setPartnerLabel(null);
      return;
    }
    const found = (partnerSelects ?? []).find((b: any) => b?.id === q.counterpartyBusinessId);
    if (found) setPartnerLabel(renderBusinessName(found, "-"));
  }, [q.counterpartyBusinessId, partnerSelects]);

  const rows: any[] = useMemo(() => (data?.rows as any[]) ?? [], [data]);
  const total = data?.count ?? 0;

  // Same selection as the open-invoice list (sales-invoice/list.tsx): survives paging, shift-click
  // selects a range, «select all matching» loads up to SELECT_ALL_CAP rows; a filter change clears it.
  const selection = useRowSelection<any>(rows);
  const { selectedRows, allPageSelected, somePageSelected, clear: clearSelection } = selection;
  const [bulkBusy, setBulkBusy] = useState<"pdf" | "selectAll" | null>(null);
  const selectAllCount = Math.min(total, SELECT_ALL_CAP);
  const canSelectAll = allPageSelected && total > rows.length && selection.count < selectAllCount;
  useEffect(() => {
    clearSelection();
  }, [apiFilter, clearSelection]);

  const handleSelectAll = async () => {
    setBulkBusy("selectAll");
    try {
      const res: any = await InvoiceApi.closeWithFee({
        offset: { page: 1, limit: selectAllCount },
        filter: apiFilter,
      } as any);
      selection.select((res?.rows as any[]) ?? []);
    } catch (err) {
      message.error(errorReason(err));
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkPdf = async () => {
    setBulkBusy("pdf");
    try {
      const failed = await saveInvoicePdfs(selectedRows);
      if (failed > 0) message.error(t("invoice:bulk.pdfFailed", { failed, count: selectedRows.length }));
    } finally {
      setBulkBusy(null);
    }
  };
  const serverSummary = (data as { summary?: Record<string, unknown> } | undefined)?.summary ?? null;

  const hasActiveFilters = !!(q.startDate || q.endDate || q.counterpartyBusinessId || q.query);

  const stripSummary = useMemo(() => {
    const closedCount = total;
    if (serverSummary && typeof serverSummary === "object") {
      const s = serverSummary as {
        totalAmount?: number;
        avgCollectionDays?: number;
        ebarimtPct?: number;
        confirmedAmount?: number;
      };
      return {
        closedCount,
        totalAmount: Number(s.totalAmount ?? s.confirmedAmount ?? 0),
        avg: Number(s.avgCollectionDays ?? 0),
        ebarimtPct: Number(s.ebarimtPct ?? 0),
        fromServer: true as const,
        pageRowCount: rows.length,
      };
    }
    const totalAmount = rows.reduce((s, x) => s + Number(x?.confirmedAmount || 0), 0);
    const spans: number[] = rows
      .map((x) => daysBetween(x?.confirmedDate, x?.paidDate))
      .filter((v): v is number => v != null && v >= 0);
    const avg = spans.length ? Math.round(spans.reduce((s, v) => s + v, 0) / spans.length) : 0;
    const ebarimtDone = rows.filter((x) => x?.ebarimtRegistered).length;
    const ebarimtPct = rows.length ? Math.round((ebarimtDone / rows.length) * 100) : 0;
    return {
      closedCount,
      totalAmount,
      avg,
      ebarimtPct,
      fromServer: false as const,
      pageRowCount: rows.length,
    };
  }, [rows, total, serverSummary]);

  const pageAmountTotal = useMemo(
    () => rows.reduce((s, x) => s + Number(x?.confirmedAmount || 0), 0),
    [rows],
  );

  const scopeHint = hasActiveFilters
    ? t("invoice:closedStrip.filteredScope")
    : t("invoice:closedStrip.allTime");
  const pageScopeHint = t("invoice:closedStrip.pageScope", { count: stripSummary.pageRowCount });
  const avgHint = t("invoice:closedStrip.avgHint");

  const clearAllFilters = () => {
    setSearchInput("");
    setPartnerSearch("");
    setSearchParams(writeQuery({
      query: "",
      startDate: null,
      endDate: null,
      counterpartyBusinessId: null,
      page: 1,
      limit: q.limit,
    }), { replace: true });
  };

  const listStates = resolveListStates(
    <ListStates
      loading={isLoading}
      error={error}
      onRetry={() => mutate()}
      empty={rows.length === 0}
      filtered={hasActiveFilters}
      query={q.query}
      onClear={clearAllFilters}
      columns={isBuyer ? COL_COUNT - 1 : COL_COUNT}
    />
  );

  const receipt = async (record: any) => {
    addEload(record.id);
    if (record.ebarimtRegistered) {
      setOpen(true);
      setPdfUrl(record.barimtUrl);
      removeEload(record.id);
    } else {
      try {
        const res = await EbarimtApi.ebarimt(record.id);
        if (res && res.status === "SUCCESS") {
          setOpen(true);
          setPdfUrl(res.url);
          setDataChanged((prev) => !prev);
          await mutate();
        }
      } catch (err) {
        handleError(err);
      } finally {
        removeEload(record.id);
        if (!record.ebarimtRegistered) setOpen(false);
      }
    }
  };

  const handleReturnClick = (record: any) => {
    modals.openConfirmModal({
      title: t("invoice:ebarimt.returnTitle"),
      children: <Text size="sm" ta="center">{t("invoice:ebarimt.returnText", { code: record.refCode })}</Text>,
      labels: { confirm: t("invoice:ebarimt.returnConfirm"), cancel: t("invoice:ebarimt.returnCancel") },
      confirmProps: { color: "red" },
      cancelProps: { variant: "default" },
      classNames: {
        title: modalChrome.confirmTitle,
        body: modalChrome.confirmModalBody,
        header: modalChrome.modalHeader,
      },
      onClose: () => removeEReturn(record.id),
      onCancel: () => removeEReturn(record.id),
      onConfirm: async () => {
        try {
          const res = await EbarimtApi.ebarimtReturn(record.id);
          if (res) {
            message.success(t("invoice:ebarimt.returnSuccess"));
            setDataChanged((prev) => !prev);
          }
        } catch (err) {
          handleError(err);
        } finally {
          removeEReturn(record.id);
        }
      },
    });
  };

  const [exporting, setExporting] = useState<"csv" | "print" | null>(null);

  const runExport = async (kind: "csv" | "print") => {
    setExporting(kind);
    try {
      const all = await InvoiceApi.closeWithFee({
        offset: { page: 1, limit: Math.min(Math.max(total, 1), EXPORT_LIMIT) },
        filter: apiFilter,
      } as any);
      const list: any[] = (all?.rows as any[]) ?? [];
      const money = (v: unknown) => currencyFormat(Number(v || 0), { withSymbol: false });
      const header = [
        t("invoice:closedCol.number"),
        isBuyer ? t("invoice:detail.party.supplier") : t("invoice:closedCol.buyer"),
        t("invoice:closedCol.createdAt"),
        t("invoice:closedCol.closedAt"),
        isBuyer ? t("invoice:closedCol.paymentDays") : t("invoice:closedCol.collectionDays"),
        t("invoice:closedCol.amount"),
        ...(isBuyer ? [] : [t("invoice:feePay.feeColumn")]),
      ];
      const body = list.map((inv) => {
        const biz = isBuyer ? inv?.senderBusiness : inv?.receiverBusiness;
        return [
          inv?.refCode,
          renderBusinessName(biz, "-"),
          dateFormat(inv?.confirmedDate),
          dateFormat(inv?.paidDate),
          daysBetween(inv?.confirmedDate, inv?.paidDate),
          money(inv?.confirmedAmount),
          ...(isBuyer ? [] : [inv?.feeAmount != null ? money(inv.feeAmount) : null]),
        ];
      });
      if (kind === "csv") {
        downloadCsv(`closed-invoices-${new Date().toISOString().slice(0, 10)}.csv`, header, body);
        return;
      }
      const sum = list.reduce((acc, inv) => acc + Number(inv?.confirmedAmount || 0), 0);
      printReport({
        title: t("invoice:closedInvoice"),
        subtitle: `${list.length} ${scopeHint}`,
        header,
        rows: body,
        numeric: isBuyer ? [4, 5] : [4, 5, 6],
        footer: [
          t("invoice:closedCol.tfootTotal", { count: list.length }),
          "",
          "",
          "",
          "",
          money(sum),
          ...(isBuyer ? [] : [""]),
        ],
      });
    } catch (err) {
      handleError(err);
    } finally {
      setExporting(null);
    }
  };

  const goDetails = (id?: string) => {
    if (id) navigate({ pathname: `/closed-invoice/${id}/details`, search: searchParams.toString() });
  };

  const partnerOptions = (partnerSelects ?? []).map((b: any) => ({
    value: b.id as string,
    label: renderBusinessName(b, "-"),
  }));

  return (
    <Page>
      <Box className={listClasses.pageHdr}>
        <Box className={listClasses.pageHdrLeft}>
          <Box className={listClasses.pageTitleRow}>
            <Title order={1} className={listClasses.pageTitle}>
              {t("invoice:closedInvoice")}
            </Title>
            {total > 0 && !error && (
              <Text component="span" className={listClasses.pageTitleChip}>
                {total}
              </Text>
            )}
          </Box>
          <Text className={listClasses.pageSub}>{t("invoice:closedInvoiceSubtitle")}</Text>
        </Box>
        <Box className={listClasses.pageHdrRight}>
          <Button
            variant="default"
            className={listClasses.actionBtn}
            loading={exporting === "csv"}
            disabled={total === 0 || exporting === "print"}
            onClick={() => runExport("csv")}
          >
            {t("invoice:closedActions.csvExport")}
          </Button>
          <Button
            variant="default"
            className={listClasses.actionBtn}
            loading={exporting === "print"}
            disabled={total === 0 || exporting === "csv"}
            onClick={() => runExport("print")}
          >
            {t("invoice:closedActions.printReport")}
          </Button>
          {!isBuyer && (
            <Tooltip
              label={t("invoice:feePay.noUnpaidFees")}
              disabled={!feeBtnDisabled}
              withArrow
            >
              <Box component="span">
                <Button
                  className={listClasses.actionBtnPrimary}
                  leftSection={<CoinsIcon size={14} />}
                  disabled={feeBtnDisabled}
                  onClick={() => setFeeModal({ open: true, scopeFeeId: null })}
                >
                  {t("invoice:feePay.title")}
                </Button>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box className={listClasses.kpiCard}>
        <Box className={listClasses.kpiCell}>
          <Text className={listClasses.kpiLabel}>{t("invoice:closedStrip.totalClosed")}</Text>
          <Text className={listClasses.kpiValue}>{error ? "-" : stripSummary.closedCount}</Text>
          <Text className={listClasses.kpiHint}>{scopeHint}</Text>
        </Box>
        <Box className={listClasses.kpiCell}>
          <Text className={listClasses.kpiLabel}>{t("invoice:closedStrip.totalAmount")}</Text>
          <Text className={listClasses.kpiValue}>
            {error ? "-" : currencyFormat(stripSummary.totalAmount, { withSymbol: false })}
          </Text>
          <Text className={listClasses.kpiHint}>
            {stripSummary.fromServer ? scopeHint : pageScopeHint}
          </Text>
        </Box>
        <Box className={listClasses.kpiCell}>
          <Text className={listClasses.kpiLabel}>
            {isBuyer ? t("invoice:closedStrip.avgDaysBuyer") : t("invoice:closedStrip.avgDays")}
          </Text>
          <Text className={listClasses.kpiValue}>
            {error ? "-" : t("invoice:closedStrip.daysValue", { count: stripSummary.avg })}
          </Text>
          <Text className={listClasses.kpiHint}>
            {stripSummary.fromServer ? avgHint : `${avgHint} · ${pageScopeHint}`}
          </Text>
        </Box>
        <Box className={listClasses.kpiCell}>
          <Text className={listClasses.kpiLabel}>{t("invoice:closedStrip.ebarimtRegistered")}</Text>
          <Text className={listClasses.kpiValue}>{error ? "-" : `${stripSummary.ebarimtPct}%`}</Text>
          <Text className={listClasses.kpiHint}>
            {stripSummary.fromServer
              ? t("invoice:closedStrip.ebarimtHint")
              : `${t("invoice:closedStrip.ebarimtHint")} · ${pageScopeHint}`}
          </Text>
        </Box>
      </Box>

      {!INV_LIST_CLOSED.hasAccess ? (
        INV_LIST_CLOSED.accessDenied()
      ) : (
        <Box className={listClasses.tableCard}>
          <Box className={listClasses.filterBar}>
            <Box className={listClasses.filterChips}>
              <DateRangeChip
                label={t("invoice:chips.closedDate")}
                value={[q.startDate, q.endDate]}
                onChange={([startDate, endDate]) => patchQuery({ startDate, endDate })}
              />

              <Popover
                opened={partnerOpen}
                onChange={setPartnerOpen}
                position="bottom-start"
                shadow="md"
                withinPortal
              >
                <Popover.Target>
                  <Box
                    component="button"
                    type="button"
                    className={`${listClasses.filterChip}${
                      partnerLabel || q.counterpartyBusinessId
                        ? ` ${listClasses.filterChipActive}`
                        : ""
                    }`}
                    onClick={() => setPartnerOpen((o) => !o)}
                  >
                    {partnerLabel || q.counterpartyBusinessId ? (
                      <>
                        <Text span size="sm" fw={500}>
                          {t(isBuyer ? "invoice:chips.supplierFilled" : "invoice:chips.partnerFilled", {
                            name: partnerLabel ?? q.counterpartyBusinessId,
                          })}
                        </Text>
                        <XIcon
                          size={12}
                          weight="bold"
                          onClick={(e) => {
                            e.stopPropagation();
                            patchQuery({ counterpartyBusinessId: null });
                            setPartnerLabel(null);
                            setPartnerOpen(false);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <PlusIcon size={12} weight="bold" />
                        <Text span size="sm">
                          {t(isBuyer ? "invoice:chips.supplier" : "invoice:chips.partner")}
                        </Text>
                      </>
                    )}
                  </Box>
                </Popover.Target>
                <Popover.Dropdown className={listClasses.partnerPopover}>
                  <TextInput
                    size="sm"
                    placeholder={t(isBuyer ? "invoice:chips.supplier" : "invoice:chips.partner")}
                    value={partnerSearch}
                    onChange={(e) => setPartnerSearch(e.currentTarget.value)}
                    mb={8}
                    autoFocus
                  />
                  {partnerLoading ? (
                    <GroupLoader />
                  ) : partnerOptions.length === 0 ? (
                    <Text size="sm" c="dimmed" py="sm" ta="center">
                      {t("common:noResults")}
                    </Text>
                  ) : (
                    <Stack gap={2} mah={240} style={{ overflowY: "auto" }}>
                      {partnerOptions.map((opt) => (
                        <UnstyledButton
                          key={opt.value}
                          className={listClasses.partnerOption}
                          onClick={() => {
                            setPartnerLabel(opt.label);
                            patchQuery({ counterpartyBusinessId: opt.value });
                            setPartnerOpen(false);
                          }}
                        >
                          {opt.label}
                        </UnstyledButton>
                      ))}
                    </Stack>
                  )}
                </Popover.Dropdown>
              </Popover>
            </Box>

            <Input
              className={listClasses.filterSearch}
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              placeholder={t("invoice:searchPlaceholder")}
              rightSection={<ListMagnifyingGlassIcon color="gray" size={16} />}
              size="sm"
            />
          </Box>

          {listStates ? (
            <Box className={listClasses.tblWrap}>{listStates}</Box>
          ) : (
            <>
              <Box className={listClasses.tblWrap}>
                <Box component="table" className={listClasses.tbl}>
                  <Box component="colgroup">
                    <Box component="col" className={listClasses.colWCheck} />
                    <Box component="col" className={listClasses.colWActions} />
                    <Box component="col" className={listClasses.colWNumber} />
                    <Box component="col" className={listClasses.colWBuyer} />
                    <Box component="col" className={listClasses.colWCreated} />
                    <Box component="col" className={listClasses.colWClosed} />
                    <Box component="col" className={listClasses.colWCollection} />
                    <Box component="col" className={listClasses.colWAmount} />
                    {!isBuyer && <Box component="col" className={listClasses.colWFee} />}
                  </Box>
                  <Box component="thead">
                    <Box component="tr" className={listSticky.scrollRow}>
                      <Box component="th" className={`${listClasses.colCheck} ${listSticky.stickyCheck}`}>
                        <Checkbox
                          color="brand"
                          size="sm"
                          radius="sm"
                          checked={allPageSelected}
                          indeterminate={somePageSelected && !allPageSelected}
                          onChange={selection.toggleAll}
                          aria-label={t("common:all")}
                        />
                      </Box>
                      <Box
                        component="th"
                        className={`${listClasses.colActions} ${listSticky.stickyActions}`}
                      >
                        <Text className={listSticky.stickyActionsHead}>{t("common:actions")}</Text>
                      </Box>
                      <Box component="th">{t("invoice:closedCol.number")}</Box>
                      <Box component="th">
                        {isBuyer ? t("invoice:detail.party.supplier") : t("invoice:closedCol.buyer")}
                      </Box>
                      <Box component="th">{t("invoice:closedCol.createdAt")}</Box>
                      <Box component="th">{t("invoice:closedCol.closedAt")}</Box>
                      <Box component="th" className={listClasses.colNum}>
                        {isBuyer ? t("invoice:closedCol.paymentDays") : t("invoice:closedCol.collectionDays")}
                      </Box>
                      <Box component="th" className={listClasses.colNum}>
                        {t("invoice:closedCol.amount")}
                      </Box>
                      {!isBuyer && (
                        <Box component="th" className={listClasses.colNum}>
                          {t("invoice:feePay.feeColumn")}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {rows.map((inv: any, i: number) => {
                            const biz =
                              myBusinessType === "BUYER" ? inv?.senderBusiness : inv?.receiverBusiness;
                            const name = renderBusinessName(biz, "-");
                            const reconciled = !!inv?.ebarimtRegistered;
                            const showEbarimt =
                              myBusinessType === "SUPPLIER" &&
                              inv.invoiceStatus === "CLOSED" &&
                              INV_LIST_CLOSED_EBARIMT.hasAccess;
                            const showEbarimtReturn = showEbarimt && reconciled;
                            const showFeePay =
                              !!inv?.collectedFeeId &&
                              inv?.feeStatus !== "SETTLED" &&
                              inv?.feeStatus !== "SETTLING";
                            const collectionDays = daysBetween(inv?.confirmedDate, inv?.paidDate);
                            const selected = selection.isSelected(inv?.id);

                            return (
                              <Box
                                component="tr"
                                key={inv.id ?? inv.refCode ?? i}
                                className={listSticky.scrollRow}
                                data-selected={selected || undefined}
                                onClick={() => goDetails(inv.id)}
                              >
                                <Box
                                  component="td"
                                  className={`${listClasses.colCheck} ${listSticky.stickyCheck}`}
                                  onClick={(e) => e.stopPropagation()}
                                  // Shift-click selects a range; keep it from also selecting page text.
                                  onMouseDown={(e) => e.shiftKey && e.preventDefault()}
                                >
                                  <Checkbox
                                    color="brand"
                                    size="sm"
                                    radius="sm"
                                    checked={selected}
                                    onChange={(e) => selection.toggle(i, (e.nativeEvent as MouseEvent).shiftKey)}
                                  />
                                </Box>
                                <Box
                                  component="td"
                                  className={`${listClasses.colActions} ${listSticky.stickyActions} ${listSticky.stickyEdge}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <RowActions
                                    items={[
                                      {
                                        key: "view",
                                        intent: "view",
                                        icon: <FileTextIcon size={17} />,
                                        label: t("invoice:tooltip.details"),
                                        onClick: () => goDetails(inv.id),
                                        disabled: !INV_LIST_CLOSED_GET.hasAccess,
                                        disabledReason: INV_LIST_CLOSED_GET.reason,
                                      },
                                      {
                                        key: "pdf",
                                        intent: "pdf",
                                        icon: <DownloadSimpleIcon size={17} />,
                                        label: t("invoice:tooltip.pdf"),
                                        onClick: () => downloadPdf(`${inv.id}`),
                                        loading: isPdfLoading(inv.id),
                                        disabled: !INV_LIST_CLOSED_PDF.hasAccess,
                                        disabledReason: INV_LIST_CLOSED_PDF.reason,
                                      },
                                      {
                                        key: "ebarimt",
                                        intent: "ebarimt",
                                        icon: (
                                          <SealCheckIcon
                                            size={18}
                                            weight={reconciled ? "fill" : "regular"}
                                          />
                                        ),
                                        label: reconciled
                                          ? t("invoice:tooltip.ebarimtView")
                                          : t("invoice:tooltip.ebarimtCreate"),
                                        onClick: () => receipt(inv),
                                        loading: isEloading(inv.id),
                                        hidden: !showEbarimt,
                                      },
                                      {
                                        key: "ebarimtReturn",
                                        intent: "danger",
                                        icon: <ArrowClockwiseIcon size={17} />,
                                        label: t("invoice:tooltip.ebarimtReturn"),
                                        onClick: () => {
                                          addEReturn(inv.id);
                                          handleReturnClick(inv);
                                        },
                                        loading: isEReturning(inv.id),
                                        hidden: !showEbarimtReturn,
                                      },
                                      {
                                        key: "feePay",
                                        intent: "default",
                                        icon: <CoinsIcon size={17} />,
                                        label: t("invoice:feePay.title"),
                                        onClick: () =>
                                          setFeeModal({
                                            open: true,
                                            scopeFeeId: inv.collectedFeeId ?? null,
                                          }),
                                        hidden: isBuyer || !showFeePay,
                                      },
                                    ]}
                                  />
                                </Box>
                                <Box component="td" className={listClasses.colWNumber}>
                                  <DocNoCell
                                    value={inv?.refCode}
                                    fw={500}
                                    color="gray.9"
                                    fz={14}
                                    showHash={false}
                                    underlineOnHover
                                    className={listClasses.docNoClosed}
                                    onClick={() => goDetails(inv.id)}
                                  />
                                </Box>
                                <Box component="td">
                                  <PartyCell
                                    name={name}
                                    logo={biz?.logo}
                                    code={biz?.refCode ?? null}
                                  />
                                </Box>
                                <Box component="td" className={listClasses.dateCreated}>
                                  <DateCell value={inv?.confirmedDate} />
                                </Box>
                                <Box component="td" className={listClasses.dateClosed}>
                                  <DateCell value={inv?.paidDate} />
                                </Box>
                                <Box component="td" className={listClasses.collectionDays}>
                                  {collectionDays != null
                                    ? t("invoice:closedStrip.daysValue", { count: collectionDays })
                                    : "-"}
                                </Box>
                                <Box
                                  component="td"
                                  className={`${listClasses.colNum} ${listClasses.amountStrong}`}
                                >
                                  <MoneyCell amount={Number(inv?.confirmedAmount || 0)} fw={600} />
                                </Box>
                                {!isBuyer && (
                                <Box component="td" className={listClasses.colNum}>
                                  {inv?.feeAmount != null ? (
                                    <Box className={listClasses.feeCell}>
                                      <MoneyCell amount={Number(inv.feeAmount || 0)} />
                                      <Text
                                        component="span"
                                        className={`${listClasses.feeMeta} ${
                                          inv?.feeStatus === "SETTLED"
                                            ? listClasses.feeMetaPaid
                                            : listClasses.feeMetaPending
                                        }`}
                                      >
                                        {inv?.feeRate != null
                                          ? `${(Number(inv.feeRate) * 100).toFixed(2)}% · `
                                          : ""}
                                        {inv?.feeStatus === "SETTLED"
                                          ? t("invoice:feePay.paid")
                                          : t("invoice:feePay.pending")}
                                      </Text>
                                    </Box>
                                  ) : (
                                    <Text component="span" className={listClasses.feeEmpty}>
                                      -
                                    </Text>
                                  )}
                                </Box>
                                )}
                              </Box>
                            );
                          })}
                  </Box>
                  {rows.length > 0 && (
                    <Box component="tfoot">
                      <Box component="tr" className={listSticky.scrollRow}>
                        <Box component="td" className={`${listClasses.colCheck} ${listSticky.stickyCheck}`} />
                        <Box
                          component="td"
                          className={`${listClasses.colActions} ${listSticky.stickyActions} ${listSticky.stickyEdge}`}
                        />
                        <Box component="td" colSpan={5}>
                          <Text className={listClasses.tfootLabel}>
                            {stripSummary.fromServer
                              ? t("invoice:closedCol.tfootTotal", { count: total })
                              : t("invoice:closedCol.tfootPageTotal", { count: rows.length })}
                          </Text>
                        </Box>
                        <Box component="td" className={listClasses.tfootAmount}>
                          {currencyFormat(
                            stripSummary.fromServer ? stripSummary.totalAmount : pageAmountTotal,
                            { withSymbol: true },
                          )}
                        </Box>
                        {!isBuyer && <Box component="td" />}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              <ListFooter
                from={Math.min(total, (q.page - 1) * q.limit + 1)}
                to={Math.min(total, (q.page - 1) * q.limit + rows.length)}
                total={total}
                page={q.page}
                pageSize={q.limit}
                onPageChange={(p) => patchQuery({ page: p }, false)}
                onPageSizeChange={(limit) => patchQuery({ limit, page: 1 })}
              />
            </>
          )}
        </Box>
      )}

      <BulkActionBar
        selectedCount={selection.count}
        onClear={clearSelection}
        selectAllAction={
          canSelectAll
            ? {
                label: t(total > SELECT_ALL_CAP ? "common:list.bulkSelectFirst" : "common:list.bulkSelectAll", {
                  count: selectAllCount,
                }),
                onClick: handleSelectAll,
                loading: bulkBusy === "selectAll",
              }
            : undefined
        }
        primaryAction={
          INV_LIST_CLOSED_PDF.hasAccess
            ? {
                label: t("invoice:actions.downloadPdf"),
                onClick: handleBulkPdf,
                loading: bulkBusy === "pdf",
              }
            : undefined
        }
      />

      <Modal
        opened={open}
        onClose={() => {
          setOpen(false);
          setPdfUrl("");
        }}
        title={
          <Box className={listClasses.modalTitleWrap}>
            <Box className={listClasses.modalTitleIcon}>
              <FileTextIcon size={18} weight="fill" />
            </Box>
            <Text fw={700} fz={14}>
              {t("invoice:ebarimt.modalTitle")}
            </Text>
          </Box>
        }
        size="90%"
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="700px"
            className={listClasses.ebarimtFrame}
            title="ebarimt"
          />
        )}
      </Modal>

      <FeePayModal
        opened={feeModal.open}
        scopeFeeId={feeModal.scopeFeeId}
        onClose={() => setFeeModal({ open: false, scopeFeeId: null })}
        onPaid={() => mutate()}
      />
    </Page>
  );
};

function GroupLoader() {
  return (
    <Box py="md" style={{ display: "flex", justifyContent: "center" }}>
      <Loader size="sm" />
    </Box>
  );
}
